import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authApi, type LoginRequest } from '../api/client';
import { isPublicPath } from '../utils/publicPaths';
import { descartarTelemetria } from '../utils/telemetria';

export const useLogin = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  return useMutation({
    mutationFn: (credentials: LoginRequest) => authApi.login(credentials),
    onSuccess: (response, credentials) => {
      const { token, usuario } = response.data;
      // Nada garante que /login só é alcançado deslogado (sem AuthGuard, um
      // usuário autenticado pode navegar direto pra cá e logar como outro) —
      // descarta antes de trocar o token pra não postar fila do usuário anterior.
      descartarTelemetria();
      localStorage.setItem('auth_token', token);
      // Lembra o login digitado pro SessionExpiredDialog pre-preencher se a
      // sessao expirar enquanto a tela esta aberta.
      localStorage.setItem('last_login', credentials.loginOuEmail);
      queryClient.setQueryData(['user'], usuario);

      // Redirecionar para a página principal após login bem-sucedido
      navigate('/', { replace: true });
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    // Audit E-03 (2026-05-20): chama o backend best-effort pra revogar o JWT
    // (usuario_token_revogado) antes de limpar o localStorage. Em até 60s qualquer
    // request com esse token cai em 401. Se o backend falhar (offline, 500,
    // token já expirado → 401), seguimos limpando localStorage normalmente
    // — UX de logout client-side não pode depender de network.
    mutationFn: async () => {
      try {
        await authApi.logout();
      } catch {
        // best-effort: ignora falha. Token expirará naturalmente no Jwt.ExpiryInMinutes.
      }
      // Logout é SPA (sem reload) — descarta a fila antes que o próximo login
      // na mesma aba a envie com o token de outro usuário.
      descartarTelemetria();
      localStorage.removeItem('auth_token');
    },
    onSuccess: () => {
      queryClient.setQueryData(['user'], null);
      queryClient.clear();
      // Redirecionar para login
      navigate('/login', { replace: true });
    },
  });
};

export const useUser = () => {
  return useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      // Skip o profile em rotas públicas — fornecedor não precisa estar logado
      // pra preencher a cotação. Evita que um auth_token stale no localStorage
      // dispare 401 e tire o user da rota pública via AuthGuard.
      if (isPublicPath(window.location.pathname)) {
        return null;
      }
      const token = localStorage.getItem('auth_token');
      if (!token) {
        return null;
      }

      try {
        const response = await authApi.getProfile();
        return response.data;
      } catch (error) {
        // Token inválido/de outro usuário (ex.: aba com token stale) — descarta
        // a fila junto, senão sobrevive pro próximo login nesta aba.
        descartarTelemetria();
        localStorage.removeItem('auth_token');
        throw error;
      }
    },
    retry: false,
  });
};

export const useIsAuthenticated = () => {
  const { data: user, isLoading } = useUser();
  return {
    isAuthenticated: !!user && !isLoading,
    user,
    isLoading,
  };
};